/**
 * `/projects` — gTwin projects landing (WP-1 refactor).
 *
 * Single landing surface per masterplan §5.1:
 *   - List of gTwins for the current tenant.
 *   - Search box (label / id substring).
 *   - Filter chips: status (all / recently-opened / imported),
 *     owner (any / mine / shared), recently-opened-only toggle.
 *   - Single primary CTA per project row: "Open project" → `/t/{id}`.
 *   - Secondary actions: Import .gtwin (file picker), New project from
 *     converter run (opens the converter picker dialog), Export .gtwin
 *     (per-row icon button via `ExportGtwinAction`).
 *   - Full state matrix: loading / empty / error / permission denied /
 *     stale (covered with `Banner` primitives + Material Symbols icons).
 *
 * Token-only styling. No raw hex / rgba / arbitrary px. Material
 * Symbols Rounded only via the `@/design` `Icon` primitive. No emoji.
 *
 * Test surface: `groupEntriesIntoModels`, `outputTypeLabel`, and
 * `ConverterFsEntry` are still exported for the existing
 * `ProjectsRoute.converterOutputs.test.ts` suite. `ProjectsRouteView` is
 * a pure presentational component used by `ProjectsRoute.states.test.tsx`
 * to exercise loading / empty / error / permission-denied state markup
 * without an HTTP round-trip.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Banner, Icon, tokens, type IconName } from '@/design';
import { gtwinClient } from './gtwinClientSingleton';
import { ExportGtwinAction } from '@/components/gtwin/ExportGtwinAction';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface GtwinFeature {
  id: string;
  properties: {
    envelope: {
      id: string;
      label: string;
      createdAt: string;
      updatedAt: string;
      domain: string;
      schema: string;
      projectAnchor?: { kind: string; horizontalCrs?: string };
      owner?: string;
      lastOpenedAt?: string;
    };
    payload?: {
      geospatial?: {
        scene?: {
          basemaps?: unknown[];
          terrain?: unknown;
          realityData?: unknown[];
          engineeringModels?: unknown[];
          featureLayers?: unknown[];
          overlays?: unknown[];
        };
      };
    };
  };
}

interface ListResponse {
  features: GtwinFeature[];
  numberMatched: number;
}

const GTWIN_API_BASE: string =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_GTWIN_API_URL) ||
  '/gtwin-api/';

const TENANT: string =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_DEFAULT_TENANT) ||
  'dev-tenant';

const CONVERTER_API_BASE: string = (
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_CONVERTER_BASE_URL) ||
  'http://localhost:3011'
).replace(/\/+$/, '');

export interface ConverterFsEntry {
  name: string;
  path: string;
  isDir: boolean;
  size?: number | null;
  mtime?: string | null;
}

interface ConverterFsListing {
  root: string;
  entries: ConverterFsEntry[];
}

interface OpenInViewerResponse {
  projectId: string;
  viewerUrl: string;
  projectLabel: string;
}

interface ImportGtwinResponse {
  id?: string;
  archiveWarnings?: string[];
  archiveIntegrity?: { warnings?: string[] };
  isFat?: boolean;
  embeddedArtifactCount?: number;
  relinkedEndpoints?: number;
}

interface TilesetMeta {
  version: string;
  generator: string;
  tileCount: number;
  lodLevels: number;
  geometricError: number;
  bbox: { cx: number; cy: number; cz: number; sx: number; sy: number; sz: number };
  totalGlbSize: number;
  hasVertexColors: boolean;
}

export interface ConverterModel {
  id: string;
  name: string;
  dirPath: string;
  tilesetEntry: ConverterFsEntry;
  allEntries: ConverterFsEntry[];
  totalSize: number;
  mtime: string | null;
  meta: TilesetMeta | null;
  metaLoading: boolean;
}

const RENDERABLE_EXTENSIONS = ['.tif', '.tiff', '.cog', '.pmtiles', '.glb', '.gltf', '.copc.laz'] as const;
const RENDERABLE_FILES = ['scene-package.json', 'tileset.json', 'layer.json', 'metadata.json'] as const;

function isRenderableArtifact(entry: ConverterFsEntry): boolean {
  if (entry.isDir) return false;
  const lower = entry.name.toLowerCase();
  if (RENDERABLE_FILES.some((n) => lower === n)) return true;
  return RENDERABLE_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return '\u2014';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function countTilesInNode(node: unknown): { tiles: number; levels: number } {
  let tiles = 0;
  let maxDepth = 0;
  function walk(n: unknown, depth: number) {
    if (!n || typeof n !== 'object') return;
    const obj = n as { content?: { uri?: string }; children?: unknown[] };
    if (obj.content?.uri) tiles++;
    maxDepth = Math.max(maxDepth, depth);
    if (Array.isArray(obj.children)) {
      for (const c of obj.children) walk(c, depth + 1);
    }
  }
  walk(node, 0);
  return { tiles, levels: maxDepth + 1 };
}

export function outputTypeLabel(entry: ConverterFsEntry): string {
  const lower = entry.name.toLowerCase();
  if (lower === 'scene-package.json') return 'Scene Package';
  if (lower === 'tileset.json') return '3D Tiles';
  if (lower === 'layer.json') return 'Terrain';
  if (lower === 'metadata.json') return 'Point Cloud / Potree';
  if (lower.endsWith('.copc.laz') || lower.endsWith('.copc')) return 'Point Cloud / COPC';
  if (lower.endsWith('.pmtiles')) return 'PMTiles';
  if (lower.endsWith('.tif') || lower.endsWith('.tiff') || lower.endsWith('.cog')) return 'COG';
  if (lower.endsWith('.glb') || lower.endsWith('.gltf')) return 'GLB/glTF';
  return entry.name.split('.').pop()?.toUpperCase() ?? 'File';
}

function outputTypeIcon(entry: ConverterFsEntry): IconName {
  const lower = entry.name.toLowerCase();
  if (lower === 'scene-package.json') return 'widgets';
  if (lower === 'tileset.json') return 'view_in_ar';
  if (lower.endsWith('.copc.laz') || lower.endsWith('.copc') || lower === 'metadata.json') {
    return 'blur_on';
  }
  if (lower.endsWith('.pmtiles')) return 'layers';
  if (lower.endsWith('.tif') || lower.endsWith('.tiff') || lower.endsWith('.cog')) return 'image_not_supported';
  if (lower === 'layer.json') return 'terrain';
  if (lower.endsWith('.glb') || lower.endsWith('.gltf')) return 'view_in_ar';
  return 'description';
}

function isTilesetEntry(entry: ConverterFsEntry): boolean {
  return entry.name.toLowerCase() === 'tileset.json';
}

function isScenePackageEntry(entry: ConverterFsEntry): boolean {
  return entry.name.toLowerCase() === 'scene-package.json';
}

export function groupEntriesIntoModels(entries: ConverterFsEntry[]): ConverterModel[] {
  const dirMap = new Map<string, ConverterFsEntry[]>();
  for (const e of entries) {
    const parts = e.path.replace(/^\/data\/out\//, '').split('/');
    const dirName = parts.length > 1 ? parts[0] : '__root__';
    if (!dirMap.has(dirName)) dirMap.set(dirName, []);
    dirMap.get(dirName)!.push(e);
  }

  const models: ConverterModel[] = [];
  for (const [dirName, files] of dirMap) {
    const scenePackageEntry = files.find((f) => isScenePackageEntry(f));
    const tilesetEntry = files.find((f) => f.name.toLowerCase() === 'tileset.json');
    const primaryEntry = scenePackageEntry ?? tilesetEntry;
    if (!primaryEntry) {
      for (const f of files) {
        if (isRenderableArtifact(f)) {
          models.push({
            id: f.path,
            name: f.name,
            dirPath: dirName,
            tilesetEntry: f,
            allEntries: [f],
            totalSize: f.size ?? 0,
            mtime: f.mtime ?? null,
            meta: null,
            metaLoading: false,
          });
        }
      }
      continue;
    }

    const totalSize = files.reduce((sum, f) => sum + (f.size ?? 0), 0);
    const latestMtime = files
      .map((f) => f.mtime)
      .filter(Boolean)
      .sort()
      .pop() ?? null;

    const displayName = dirName === '__root__'
      ? primaryEntry.name
      : dirName.replace(/_tiles$/i, '').replace(/_3dtiles$/i, '').replace(/_streaming_tiles$/i, ' (streaming)');

    models.push({
      id: primaryEntry.path,
      name: displayName,
      dirPath: dirName,
      tilesetEntry: primaryEntry,
      allEntries: files,
      totalSize,
      mtime: latestMtime,
      meta: null,
      metaLoading: false,
    });
  }

  return models.sort((a, b) => {
    if (a.mtime && b.mtime) return b.mtime.localeCompare(a.mtime);
    return a.name.localeCompare(b.name);
  });
}

async function fetchTilesetMeta(entry: ConverterFsEntry, allEntries: ConverterFsEntry[]): Promise<TilesetMeta | null> {
  try {
    const relPath = entry.path.replace(/^\/data\/out\//, '');
    const baseUrl = `${CONVERTER_API_BASE}/converter-output/`;
    const tilesetUrl = `${baseUrl}${relPath.split('/').map(encodeURIComponent).join('/')}`;
    const resp = await fetch(tilesetUrl);
    if (!resp.ok) return null;
    const json = await resp.json();
    const root = json.root;
    if (!root) return null;

    const { tiles, levels } = countTilesInNode(root);
    const box = root.boundingVolume?.box;
    const bbox = box && box.length >= 12
      ? { cx: box[0], cy: box[1], cz: box[2], sx: Math.abs(box[3]) * 2, sy: Math.abs(box[7]) * 2, sz: Math.abs(box[11]) * 2 }
      : { cx: 0, cy: 0, cz: 0, sx: 0, sy: 0, sz: 0 };

    const glbEntries = allEntries.filter((e) => e.name.toLowerCase().endsWith('.glb'));
    const totalGlbSize = glbEntries.reduce((s, e) => s + (e.size ?? 0), 0);

    let hasVertexColors = false;
    const firstGlbUri = root.content?.uri;
    if (firstGlbUri) {
      try {
        const dirPath = relPath.substring(0, relPath.lastIndexOf('/'));
        const glbUrl = `${baseUrl}${dirPath}/${firstGlbUri.split('/').map(encodeURIComponent).join('/')}`;
        const glbResp = await fetch(glbUrl, { headers: { Range: 'bytes=0-8192' } });
        if (glbResp.ok || glbResp.status === 206) {
          const buf = await glbResp.arrayBuffer();
          const textSlice = new TextDecoder().decode(new Uint8Array(buf, 20, Math.min(buf.byteLength - 20, 6000)));
          hasVertexColors = textSlice.includes('COLOR_0');
        }
      } catch { /* non-fatal */ }
    }

    return {
      version: json.asset?.version ?? '?',
      generator: json.asset?.generator ?? 'unknown',
      tileCount: tiles,
      lodLevels: levels,
      geometricError: json.geometricError ?? 0,
      bbox,
      totalGlbSize,
      hasVertexColors,
    };
  } catch {
    return null;
  }
}

function countLayers(g: GtwinFeature): number {
  const scene = (g.properties.payload?.geospatial?.scene ?? {}) as Record<string, unknown>;
  let n = 0;
  for (const slot of ['basemaps', 'terrain', 'realityData', 'engineeringModels', 'featureLayers', 'overlays'] as const) {
    const value = scene[slot];
    if (Array.isArray(value)) n += value.length;
    else if (slot === 'terrain' && value) n += 1;
  }
  return n;
}

// ---------------------------------------------------------------------------
// Filter contract
// ---------------------------------------------------------------------------

export type ProjectsStatusFilter = 'all' | 'recently-opened' | 'imported';
export type ProjectsOwnerFilter = 'any' | 'mine' | 'shared';

export interface ProjectsFilterState {
  readonly query: string;
  readonly status: ProjectsStatusFilter;
  readonly owner: ProjectsOwnerFilter;
  readonly recentOnly: boolean;
}

const DEFAULT_FILTERS: ProjectsFilterState = {
  query: '',
  status: 'all',
  owner: 'any',
  recentOnly: false,
};

export function applyProjectFilters(
  projects: readonly GtwinFeature[],
  filters: ProjectsFilterState,
  options: { now?: number } = {},
): GtwinFeature[] {
  const now = options.now ?? Date.now();
  const recentMs = 7 * 24 * 60 * 60 * 1000;
  const queryLc = filters.query.trim().toLowerCase();
  return projects.filter((g) => {
    const env = g.properties.envelope;
    if (queryLc.length > 0) {
      const haystack = `${env.label ?? ''} ${env.id ?? ''}`.toLowerCase();
      if (!haystack.includes(queryLc)) return false;
    }
    if (filters.status === 'recently-opened') {
      const lastOpen = env.lastOpenedAt ? Date.parse(env.lastOpenedAt) : Date.parse(env.updatedAt);
      if (!Number.isFinite(lastOpen) || now - lastOpen > recentMs) return false;
    }
    if (filters.status === 'imported') {
      const tags = (env as { tags?: readonly string[] }).tags ?? [];
      if (!tags.includes('imported')) return false;
    }
    if (filters.owner === 'mine') {
      if ((env.owner ?? '').toLowerCase() !== TENANT.toLowerCase()) return false;
    }
    if (filters.owner === 'shared') {
      if ((env.owner ?? '').toLowerCase() === TENANT.toLowerCase()) return false;
    }
    if (filters.recentOnly) {
      const updated = Date.parse(env.updatedAt);
      if (!Number.isFinite(updated) || now - updated > recentMs) return false;
    }
    return true;
  });
}

// ---------------------------------------------------------------------------
// View state contract
// ---------------------------------------------------------------------------

export type ProjectsViewLoadState =
  | { readonly status: 'loading' }
  | { readonly status: 'permission-denied'; readonly message?: string }
  | { readonly status: 'error'; readonly message: string }
  | { readonly status: 'loaded'; readonly projects: readonly GtwinFeature[]; readonly stale?: boolean };

// ---------------------------------------------------------------------------
// ProjectsRoute (smart container)
// ---------------------------------------------------------------------------

const STALE_THRESHOLD_MS = 5 * 60 * 1000;

export function ProjectsRoute() {
  const navigate = useNavigate();
  const [loadState, setLoadState] = useState<ProjectsViewLoadState>({ status: 'loading' });
  const [filters, setFilters] = useState<ProjectsFilterState>(DEFAULT_FILTERS);
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [converterPickerOpen, setConverterPickerOpen] = useState(false);
  const [converterModels, setConverterModels] = useState<ConverterModel[] | null>(null);
  const [converterError, setConverterError] = useState<string | null>(null);
  const [openingPath, setOpeningPath] = useState<string | null>(null);
  const [lastFetchedAt, setLastFetchedAt] = useState<number | null>(null);

  const reload = useCallback(async () => {
    setLoadState({ status: 'loading' });
    try {
      const url = `${GTWIN_API_BASE.replace(/\/+$/, '')}/api/v1/gtwins?domain=geospatial&limit=100`;
      const r = await fetch(url, { headers: { 'X-Tenant-Id': TENANT } });
      if (r.status === 401 || r.status === 403) {
        setLoadState({
          status: 'permission-denied',
          message: `Tenant "${TENANT}" is not authorized to list projects (HTTP ${r.status}).`,
        });
        return;
      }
      if (!r.ok) {
        setLoadState({ status: 'error', message: `API error ${r.status}: ${await r.text()}` });
        return;
      }
      const body = (await r.json()) as ListResponse;
      setLoadState({ status: 'loaded', projects: body.features ?? [] });
      setLastFetchedAt(Date.now());
    } catch (e) {
      setLoadState({ status: 'error', message: e instanceof Error ? e.message : String(e) });
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Mark stale after threshold even if no error.
  useEffect(() => {
    if (loadState.status !== 'loaded') return;
    if (!lastFetchedAt) return;
    const id = window.setTimeout(() => {
      setLoadState((prev) =>
        prev.status === 'loaded' && !prev.stale ? { ...prev, stale: true } : prev,
      );
    }, STALE_THRESHOLD_MS);
    return () => window.clearTimeout(id);
  }, [loadState, lastFetchedAt]);

  const loadConverterOutputs = useCallback(async () => {
    setConverterError(null);
    setConverterModels(null);
    try {
      const r = await fetch(`${CONVERTER_API_BASE}/api/v1/converter/fs/outputs`);
      if (!r.ok) {
        setConverterError(`Converter API ${r.status}: ${await r.text()}`);
        setConverterModels([]);
        return;
      }
      const body = (await r.json()) as ConverterFsListing;
      const allEntries = body.entries ?? [];
      const models = groupEntriesIntoModels(allEntries);
      setConverterModels(models);
      for (const model of models) {
        if (isTilesetEntry(model.tilesetEntry)) {
          fetchTilesetMeta(model.tilesetEntry, model.allEntries).then((meta) => {
            setConverterModels((prev) =>
              prev?.map((m) => (m.id === model.id ? { ...m, meta, metaLoading: false } : m)) ?? null,
            );
          });
        }
      }
    } catch (e) {
      setConverterError(e instanceof Error ? e.message : String(e));
      setConverterModels([]);
    }
  }, []);

  const openConverterArtifact = useCallback(
    async (model: ConverterModel) => {
      setOpeningPath(model.tilesetEntry.path);
      setConverterError(null);
      try {
        const r = await fetch(
          `${CONVERTER_API_BASE}/api/v1/converter/outputs/open-in-viewer`,
          {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ path: model.tilesetEntry.path }),
          },
        );
        if (!r.ok) {
          setConverterError(`Failed to open ${model.name}: ${r.status} ${await r.text()}`);
          setOpeningPath(null);
          return;
        }
        const body = (await r.json()) as OpenInViewerResponse;
        setConverterPickerOpen(false);
        setOpeningPath(null);
        navigate(`/t/${body.projectId}`);
      } catch (e) {
        setConverterError(e instanceof Error ? e.message : String(e));
        setOpeningPath(null);
      }
    },
    [navigate],
  );

  const onImport = useCallback(
    async (file: File) => {
      setImporting(true);
      setImportMsg(null);
      setImportError(null);
      try {
        // Fat archives may need artifact endpoint re-linking. The converter
        // output URL is used as the base for re-linked artifact paths.
        const converterBase = (import.meta.env?.VITE_CONVERTER_BASE_URL as string | undefined) || '';
        const relinkParam = converterBase ? `&relinkBase=${encodeURIComponent(converterBase.replace(/\/+$/, '') + '/converter-output')}` : '';
        const url = `${GTWIN_API_BASE.replace(/\/+$/, '')}/api/v1/gtwins/import?mode=create${relinkParam}`;
        const fd = new FormData();
        fd.append('file', file);
        const r = await fetch(url, {
          method: 'POST',
          headers: { 'X-Tenant-Id': TENANT },
          body: fd,
        });
        if (!r.ok) {
          setImportError(`Import failed: ${r.status} ${await r.text()}`);
          return;
        }
        const body = (await r.json()) as ImportGtwinResponse;
        const warnings = [...(body.archiveWarnings ?? []), ...(body.archiveIntegrity?.warnings ?? [])];
        const warningText = warnings.length > 0 ? ` Integrity warnings: ${warnings.length}.` : ' Integrity report clean.';
        const fatText = body.isFat ? ` Fat archive: ${body.embeddedArtifactCount ?? 0} artifact(s), ${body.relinkedEndpoints ?? 0} re-linked.` : '';
        if (body.id) {
          setImportMsg(`Imported ${body.id}; opening…${warningText}${fatText}`);
          navigate(`/t/${body.id}`);
        } else {
          setImportMsg(`Imported (id unknown).${warningText}${fatText} Reload the list to find it.`);
          await reload();
        }
      } catch (e) {
        setImportError(e instanceof Error ? e.message : String(e));
      } finally {
        setImporting(false);
      }
    },
    [navigate, reload],
  );

  return (
    <ProjectsRouteView
      loadState={loadState}
      filters={filters}
      onFiltersChange={setFilters}
      onReload={() => void reload()}
      importing={importing}
      importMsg={importMsg}
      importError={importError}
      onImport={onImport}
      onOpenConverterPicker={() => {
        setConverterPickerOpen(true);
        void loadConverterOutputs();
      }}
      converterPickerOpen={converterPickerOpen}
      converterModels={converterModels}
      converterError={converterError}
      openingPath={openingPath}
      onConverterClose={() => setConverterPickerOpen(false)}
      onConverterReload={loadConverterOutputs}
      onConverterSelect={openConverterArtifact}
    />
  );
}

// ---------------------------------------------------------------------------
// ProjectsRouteView (presentational; pure)
// ---------------------------------------------------------------------------

export interface ProjectsRouteViewProps {
  readonly loadState: ProjectsViewLoadState;
  readonly filters: ProjectsFilterState;
  readonly onFiltersChange: (next: ProjectsFilterState) => void;
  readonly onReload: () => void;
  readonly importing: boolean;
  readonly importMsg: string | null;
  readonly importError: string | null;
  readonly onImport: (file: File) => void | Promise<void>;
  readonly onOpenConverterPicker: () => void;
  readonly converterPickerOpen: boolean;
  readonly converterModels: ConverterModel[] | null;
  readonly converterError: string | null;
  readonly openingPath: string | null;
  readonly onConverterClose: () => void;
  readonly onConverterReload: () => void;
  readonly onConverterSelect: (model: ConverterModel) => void;
}

const filterChipStyle = (active: boolean): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: tokens.space[1],
  padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
  borderRadius: tokens.radius.pill,
  border: tokens.border.thin,
  background: active ? tokens.color.accent.subtle : tokens.color.surface[1],
  color: active ? tokens.color.accent.primary : tokens.color.ink.secondary,
  fontFamily: tokens.font.family.ui,
  fontSize: tokens.font.size.sm,
  cursor: 'pointer',
  minHeight: tokens.density.touchMin,
  fontWeight: active ? tokens.font.weight.medium : tokens.font.weight.regular,
});

export function ProjectsRouteView({
  loadState,
  filters,
  onFiltersChange,
  onReload,
  importing,
  importMsg,
  importError,
  onImport,
  onOpenConverterPicker,
  converterPickerOpen,
  converterModels,
  converterError,
  openingPath,
  onConverterClose,
  onConverterReload,
  onConverterSelect,
}: ProjectsRouteViewProps) {
  const projectsAll = useMemo(
    () =>
      loadState.status === 'loaded' ? loadState.projects : ([] as readonly GtwinFeature[]),
    [loadState],
  );
  const filtered = useMemo(
    () => applyProjectFilters(projectsAll, filters),
    [projectsAll, filters],
  );

  return (
    <main
      style={{
        minHeight: '100vh',
        background: tokens.color.surface[0],
        color: tokens.color.ink.primary,
        fontFamily: tokens.font.family.ui,
        padding: tokens.space[5],
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.space[4],
      }}
      data-route="/projects"
      data-testid="projects-route-view"
    >
      <ProjectsHeader
        importing={importing}
        onImport={onImport}
        onOpenConverterPicker={onOpenConverterPicker}
      />
      <ProjectsFilters
        filters={filters}
        onChange={onFiltersChange}
        onReload={onReload}
        loading={loadState.status === 'loading'}
      />

      {importMsg ? (
        <Banner
          severity="info"
          title="Import in progress"
          body={importMsg}
        />
      ) : null}
      {importError ? (
        <Banner
          severity="error"
          title="Import failed"
          body={importError}
          errorCode="WP1-IMPORT-GTWIN"
        />
      ) : null}

      <ProjectsBody
        loadState={loadState}
        filtered={filtered}
        totalCount={projectsAll.length}
        onReload={onReload}
        onImport={onImport}
        onOpenConverterPicker={onOpenConverterPicker}
      />

      {converterPickerOpen ? (
        <ConverterPicker
          models={converterModels}
          error={converterError}
          openingPath={openingPath}
          onClose={onConverterClose}
          onSelect={onConverterSelect}
          onReload={onConverterReload}
        />
      ) : null}
    </main>
  );
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

function ProjectsHeader({
  importing,
  onImport,
  onOpenConverterPicker,
}: {
  readonly importing: boolean;
  readonly onImport: (file: File) => void | Promise<void>;
  readonly onOpenConverterPicker: () => void;
}) {
  return (
    <header
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: tokens.space[4],
        flexWrap: 'wrap',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: tokens.space[3] }}>
        <Icon name="widgets" size={28} decorative style={{ color: tokens.color.accent.primary }} />
        <div>
          <h1
            style={{
              margin: 0,
              fontSize: tokens.font.size['2xl'],
              fontWeight: tokens.font.weight.semibold,
              fontFamily: tokens.font.family.ui,
            }}
          >
            Projects
          </h1>
          <p
            style={{
              margin: 0,
              color: tokens.color.ink.secondary,
              fontSize: tokens.font.size.sm,
            }}
          >
            Open a gTwin project, import a `.gtwin` archive, or start one from a
            converter run.
          </p>
        </div>
      </div>
      <div style={{ display: 'inline-flex', gap: tokens.space[3], alignItems: 'center', flexWrap: 'wrap' }}>
        <Link
          to="/admin/estate"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: tokens.space[2],
            padding: `${tokens.space[2]}px ${tokens.space[4]}px`,
            background: tokens.color.surface[1],
            color: tokens.color.ink.primary,
            border: tokens.border.thin,
            borderRadius: tokens.radius.input,
            textDecoration: 'none',
            fontSize: tokens.font.size.md,
            fontFamily: tokens.font.family.ui,
            fontWeight: tokens.font.weight.medium,
            minHeight: tokens.density.touchMin,
          }}
        >
          <Icon name="account_tree" size={20} decorative />
          Tenant &amp; assets
        </Link>
        <button
          type="button"
          onClick={onOpenConverterPicker}
          aria-label="Start a new project from a converter run"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: tokens.space[2],
            padding: `${tokens.space[2]}px ${tokens.space[4]}px`,
            background: tokens.color.surface[1],
            color: tokens.color.ink.primary,
            border: tokens.border.thin,
            borderRadius: tokens.radius.input,
            cursor: 'pointer',
            fontSize: tokens.font.size.md,
            fontFamily: tokens.font.family.ui,
            fontWeight: tokens.font.weight.medium,
            minHeight: tokens.density.touchMin,
          }}
          data-testid="projects-header-converter"
        >
          <Icon name="extension" size={20} decorative />
          New from converter run
        </button>
        <label
          aria-label="Import a .gtwin archive"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: tokens.space[2],
            padding: `${tokens.space[2]}px ${tokens.space[4]}px`,
            background: tokens.color.accent.primary,
            color: tokens.color.accent.on,
            borderRadius: tokens.radius.input,
            cursor: importing ? 'wait' : 'pointer',
            fontSize: tokens.font.size.md,
            fontFamily: tokens.font.family.ui,
            fontWeight: tokens.font.weight.medium,
            opacity: importing ? 0.6 : 1,
            border: `${tokens.border.width.thin}px solid transparent`,
            minHeight: tokens.density.touchMin,
          }}
          data-testid="projects-header-import"
        >
          <Icon name="upload_file" size={20} decorative />
          {importing ? 'Importing…' : 'Import .gtwin'}
          <input
            type="file"
            accept=".gtwin,.zip,application/zip"
            disabled={importing}
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void onImport(f);
            }}
          />
        </label>
      </div>
    </header>
  );
}

// ---------------------------------------------------------------------------
// Filter chip strip
// ---------------------------------------------------------------------------

function ProjectsFilters({
  filters,
  onChange,
  onReload,
  loading,
}: {
  readonly filters: ProjectsFilterState;
  readonly onChange: (next: ProjectsFilterState) => void;
  readonly onReload: () => void;
  readonly loading: boolean;
}) {
  return (
    <div
      role="toolbar"
      aria-label="Project filters"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: tokens.space[3],
        flexWrap: 'wrap',
      }}
    >
      <label
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: tokens.space[2],
          padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
          background: tokens.color.surface[1],
          border: tokens.border.thin,
          borderRadius: tokens.radius.pill,
          minHeight: tokens.density.touchMin,
        }}
      >
        <Icon name="search" size={20} decorative />
        <input
          type="search"
          placeholder="Search projects by label or id"
          value={filters.query}
          onChange={(event) =>
            onChange({ ...filters, query: event.currentTarget.value })
          }
          aria-label="Search projects"
          style={{
            border: 'none',
            outline: 'none',
            background: 'transparent',
            color: tokens.color.ink.primary,
            fontFamily: tokens.font.family.ui,
            fontSize: tokens.font.size.sm,
            minWidth: 220,
          }}
          data-testid="projects-search"
        />
      </label>

      <FilterChip
        label="All"
        icon="filter_list"
        active={filters.status === 'all'}
        onClick={() => onChange({ ...filters, status: 'all' })}
      />
      <FilterChip
        label="Recently opened"
        icon="schedule"
        active={filters.status === 'recently-opened'}
        onClick={() =>
          onChange({
            ...filters,
            status: filters.status === 'recently-opened' ? 'all' : 'recently-opened',
          })
        }
      />
      <FilterChip
        label="Imported"
        icon="upload_file"
        active={filters.status === 'imported'}
        onClick={() =>
          onChange({
            ...filters,
            status: filters.status === 'imported' ? 'all' : 'imported',
          })
        }
      />
      <FilterChip
        label="Owner: any"
        icon="business"
        active={filters.owner !== 'any'}
        onClick={() =>
          onChange({
            ...filters,
            owner:
              filters.owner === 'any'
                ? 'mine'
                : filters.owner === 'mine'
                  ? 'shared'
                  : 'any',
          })
        }
      />
      <FilterChip
        label="Updated < 7d"
        icon="history"
        active={filters.recentOnly}
        onClick={() => onChange({ ...filters, recentOnly: !filters.recentOnly })}
      />

      <button
        type="button"
        onClick={onReload}
        aria-label="Reload projects"
        title="Reload projects"
        disabled={loading}
        style={{
          marginLeft: 'auto',
          display: 'inline-flex',
          alignItems: 'center',
          gap: tokens.space[1],
          padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
          background: tokens.color.surface[1],
          color: tokens.color.ink.primary,
          border: tokens.border.thin,
          borderRadius: tokens.radius.input,
          cursor: loading ? 'wait' : 'pointer',
          fontFamily: tokens.font.family.ui,
          fontSize: tokens.font.size.sm,
          minHeight: tokens.density.touchMin,
        }}
      >
        <Icon name="refresh" size={20} decorative />
        Reload
      </button>
    </div>
  );
}

function FilterChip({
  label,
  icon,
  active,
  onClick,
}: {
  readonly label: string;
  readonly icon: IconName;
  readonly active: boolean;
  readonly onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={filterChipStyle(active)}
    >
      <Icon name={icon} size={16} decorative />
      <span>{label}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Body (loading / empty / error / permission / loaded)
// ---------------------------------------------------------------------------

function ProjectsBody({
  loadState,
  filtered,
  totalCount,
  onReload,
  onImport,
  onOpenConverterPicker,
}: {
  readonly loadState: ProjectsViewLoadState;
  readonly filtered: readonly GtwinFeature[];
  readonly totalCount: number;
  readonly onReload: () => void;
  readonly onImport: (file: File) => void | Promise<void>;
  readonly onOpenConverterPicker: () => void;
}) {
  if (loadState.status === 'loading') {
    return (
      <section
        role="status"
        aria-live="polite"
        data-testid="projects-loading"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: tokens.space[2],
          color: tokens.color.ink.secondary,
          fontSize: tokens.font.size.sm,
        }}
      >
        <Icon name="refresh" size={20} decorative />
        Loading projects…
      </section>
    );
  }

  if (loadState.status === 'permission-denied') {
    return (
      <Banner
        severity="warning"
        title="You do not have access to projects in this tenant"
        body={
          loadState.message
            ?? 'Sign in with an account that has the GeoViewer reader role, or switch tenants from the account menu.'
        }
        errorCode="WP1-PROJECTS-PERMISSION"
        actions={[
          { label: 'Reload', onClick: onReload },
        ]}
        data-testid="projects-permission"
      />
    );
  }

  if (loadState.status === 'error') {
    return (
      <Banner
        severity="error"
        title="Could not load projects"
        body={loadState.message}
        errorCode="WP1-PROJECTS-ERROR"
        actions={[{ label: 'Retry', onClick: onReload }]}
        data-testid="projects-error"
      />
    );
  }

  if (totalCount === 0) {
    return (
      <ProjectsEmptyState
        onImport={onImport}
        onOpenConverterPicker={onOpenConverterPicker}
      />
    );
  }

  return (
    <section data-testid="projects-loaded" style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[3] }}>
      {loadState.stale ? (
        <Banner
          severity="info"
          title="The list may be out of date"
          body="No refresh in the last 5 minutes. Click Reload to fetch the latest projects."
          actions={[{ label: 'Reload', onClick: onReload }]}
          data-testid="projects-stale"
        />
      ) : null}

      {filtered.length === 0 ? (
        <p
          role="status"
          aria-live="polite"
          style={{
            color: tokens.color.ink.secondary,
            fontSize: tokens.font.size.sm,
            margin: 0,
          }}
          data-testid="projects-no-matches"
        >
          No projects match the current search and filter combination.
        </p>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(auto-fill, minmax(${tokens.layout.popoverLg}px, 1fr))`,
            gap: tokens.space[3],
          }}
        >
          {filtered.map((g) => (
            <ProjectCard key={g.properties.envelope.id} feature={g} />
          ))}
        </div>
      )}
    </section>
  );
}

function ProjectsEmptyState({
  onImport,
  onOpenConverterPicker,
}: {
  readonly onImport: (file: File) => void | Promise<void>;
  readonly onOpenConverterPicker: () => void;
}) {
  return (
    <section
      data-testid="projects-empty"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: tokens.space[3],
        padding: tokens.space[6],
        background: tokens.color.surface[1],
        border: tokens.border.thin,
        borderRadius: tokens.radius.lg,
        textAlign: 'center',
      }}
    >
      <Icon
        name="widgets"
        size={32}
        decorative
        style={{ color: tokens.color.accent.primary }}
      />
      <h2
        style={{
          margin: 0,
          fontFamily: tokens.font.family.ui,
          fontSize: tokens.font.size.xl,
          fontWeight: tokens.font.weight.semibold,
        }}
      >
        No gTwin projects yet
      </h2>
      <p
        style={{
          margin: 0,
          color: tokens.color.ink.secondary,
          fontSize: tokens.font.size.md,
          maxWidth: 480,
        }}
      >
        Import an existing <code>.gtwin</code> archive, or build a new project
        from the GeoConverter run output catalog.
      </p>
      <div style={{ display: 'inline-flex', gap: tokens.space[3], flexWrap: 'wrap', justifyContent: 'center' }}>
        <label
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: tokens.space[2],
            padding: `${tokens.space[2]}px ${tokens.space[4]}px`,
            background: tokens.color.accent.primary,
            color: tokens.color.accent.on,
            borderRadius: tokens.radius.input,
            cursor: 'pointer',
            fontFamily: tokens.font.family.ui,
            fontSize: tokens.font.size.md,
            fontWeight: tokens.font.weight.medium,
            border: `${tokens.border.width.thin}px solid transparent`,
            minHeight: tokens.density.touchMin,
          }}
        >
          <Icon name="upload_file" size={20} decorative />
          Import .gtwin
          <input
            type="file"
            accept=".gtwin,.zip,application/zip"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void onImport(f);
            }}
          />
        </label>
        <button
          type="button"
          onClick={onOpenConverterPicker}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: tokens.space[2],
            padding: `${tokens.space[2]}px ${tokens.space[4]}px`,
            background: tokens.color.surface[2],
            color: tokens.color.ink.primary,
            border: tokens.border.thin,
            borderRadius: tokens.radius.input,
            cursor: 'pointer',
            fontFamily: tokens.font.family.ui,
            fontSize: tokens.font.size.md,
            fontWeight: tokens.font.weight.medium,
            minHeight: tokens.density.touchMin,
          }}
        >
          <Icon name="extension" size={20} decorative />
          New from converter run
        </button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Project card
// ---------------------------------------------------------------------------

function ProjectCard({ feature }: { readonly feature: GtwinFeature }) {
  const env = feature.properties.envelope;
  const anchor = env.projectAnchor;
  const layerCount = countLayers(feature);
  return (
    <article
      data-testid={`project-card-${env.id}`}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.space[2],
        padding: tokens.space[4],
        background: tokens.color.surface[1],
        border: tokens.border.thin,
        borderRadius: tokens.radius.lg,
        boxShadow: tokens.elevation[1],
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: tokens.space[2] }}>
        <Icon name="map" size={20} decorative style={{ color: tokens.color.accent.primary }} />
        {/* WP-15 — page lands with `<h1>Projects</h1>` and no `<h2>`
            between it and the project-card heading, so this header
            must be `<h2>` to satisfy the WCAG 2.4.6 / axe
            `heading-order` rule (no skipping levels). */}
        <h2
          style={{
            margin: 0,
            fontFamily: tokens.font.family.ui,
            fontSize: tokens.font.size.lg,
            fontWeight: tokens.font.weight.semibold,
            color: tokens.color.ink.primary,
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {env.label || '(unlabeled)'}
        </h2>
      </div>
      <code
        style={{
          color: tokens.color.ink.muted,
          fontSize: tokens.font.size.xs,
          fontFamily: tokens.font.family.mono,
        }}
      >
        {env.id}
      </code>

      <dl
        style={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          gap: `${tokens.space[1]}px ${tokens.space[3]}px`,
          margin: 0,
          fontSize: tokens.font.size.xs,
          color: tokens.color.ink.secondary,
        }}
      >
        <DescTerm icon="pin_drop" label="Anchor" />
        <dd style={ddStyle}>
          {anchor?.kind === 'projected'
            ? `${anchor.horizontalCrs ?? '?'} (projected)`
            : (anchor?.kind ?? '\u2014')}
        </dd>
        <DescTerm icon="layers" label="Layers" />
        <dd style={ddStyle}>{layerCount}</dd>
        <DescTerm icon="schedule" label="Updated" />
        <dd style={ddStyle}>
          <time dateTime={env.updatedAt}>
            {(() => {
              try {
                return new Date(env.updatedAt).toLocaleString();
              } catch {
                return env.updatedAt;
              }
            })()}
          </time>
        </dd>
      </dl>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: tokens.space[2],
          marginTop: tokens.space[2],
        }}
      >
        <Link
          to={`/t/${env.id}`}
          aria-label={`Open project ${env.label || env.id}`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: tokens.space[2],
            padding: `${tokens.space[2]}px ${tokens.space[4]}px`,
            background: tokens.color.accent.primary,
            color: tokens.color.accent.on,
            borderRadius: tokens.radius.input,
            textDecoration: 'none',
            fontFamily: tokens.font.family.ui,
            fontWeight: tokens.font.weight.medium,
            fontSize: tokens.font.size.sm,
            minHeight: tokens.density.touchMin,
          }}
          data-testid={`project-card-open-${env.id}`}
        >
          <Icon name="open_in_new" size={20} decorative />
          Open project
        </Link>
        <CloneProjectAction gtwinId={env.id} projectLabel={env.label} />
        <ExportGtwinAction
          gtwinId={env.id}
          projectLabel={env.label}
          packageAdapter={gtwinClient.package}
          mode="thin"
          data-testid={`project-card-export-${env.id}`}
        />
        <ExportGtwinAction
          gtwinId={env.id}
          projectLabel={env.label}
          packageAdapter={gtwinClient.package}
          mode="fat"
          data-testid={`project-card-export-fat-${env.id}`}
        />
      </div>
    </article>
  );
}

function CloneProjectAction({
  gtwinId,
  projectLabel,
}: {
  readonly gtwinId: string;
  readonly projectLabel: string;
}) {
  const [busy, setBusy] = useState(false);
  const handleClone = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const apiBase = (import.meta.env?.VITE_GTWIN_API_URL as string | undefined) || `${window.location.origin}/gtwin-api/`;
      const resp = await fetch(
        `${apiBase.replace(/\/+$/, '')}/api/v1/gtwins/${gtwinId}/clone`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            label: `${projectLabel} (copy)`,
            createdBy: 'viewer-user',
          }),
        },
      );
      if (resp.ok) {
        await resp.json();
        window.location.reload();
      }
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={handleClone}
      disabled={busy}
      aria-label={`Duplicate project ${projectLabel}`}
      title="Duplicate project"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: tokens.space[1],
        padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
        background: 'transparent',
        color: tokens.color.ink.secondary,
        border: tokens.border.thin,
        borderRadius: tokens.radius.input,
        cursor: busy ? 'wait' : 'pointer',
        fontSize: tokens.font.size.xs,
        minHeight: tokens.density.touchMin,
      }}
    >
      <Icon name="content_copy" size={16} decorative />
      {busy ? 'Cloning...' : 'Duplicate'}
    </button>
  );
}

const ddStyle: React.CSSProperties = {
  margin: 0,
  color: tokens.color.ink.primary,
};

function DescTerm({ icon, label }: { readonly icon: IconName; readonly label: string }) {
  return (
    <dt
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: tokens.space[1],
        color: tokens.color.ink.muted,
      }}
    >
      <Icon name={icon} size={16} decorative />
      <span>{label}</span>
    </dt>
  );
}

// ---------------------------------------------------------------------------
// Converter picker (dialog over the projects landing)
// ---------------------------------------------------------------------------

interface ConverterPickerProps {
  models: ConverterModel[] | null;
  error: string | null;
  openingPath: string | null;
  onClose: () => void;
  onSelect: (model: ConverterModel) => void;
  onReload: () => void;
}

function ModelCard({ model, opening, onSelect }: { model: ConverterModel; opening: boolean; onSelect: () => void }) {
  const meta = model.meta;
  const isTileset = isTilesetEntry(model.tilesetEntry);
  const fileCount = model.allEntries.filter((e) => !e.isDir).length;
  const typeLabel = outputTypeLabel(model.tilesetEntry);
  const typeIcon = outputTypeIcon(model.tilesetEntry);

  return (
    <div
      style={{
        background: tokens.color.surface[2],
        borderRadius: tokens.radius.lg,
        border: tokens.border.thin,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          height: 72,
          background: tokens.color.surface[3],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        <Icon name={typeIcon} size={28} decorative style={{ color: tokens.color.ink.muted }} />
        <span
          style={{
            position: 'absolute',
            top: tokens.space[1],
            right: tokens.space[1],
            display: 'inline-flex',
            alignItems: 'center',
            gap: tokens.space[1],
            padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
            borderRadius: tokens.radius.sm,
            background: isTileset ? tokens.color.accent.primary : tokens.color.surface[1],
            color: isTileset ? tokens.color.accent.on : tokens.color.ink.primary,
            fontSize: tokens.font.size.xs,
            fontWeight: tokens.font.weight.semibold,
            letterSpacing: '0.05em',
          }}
        >
          <Icon name={typeIcon} size={16} decorative />
          {typeLabel}
        </span>
        {meta && !meta.hasVertexColors ? (
          <span
            style={{
              position: 'absolute',
              top: tokens.space[1],
              left: tokens.space[1],
              display: 'inline-flex',
              alignItems: 'center',
              gap: tokens.space[1],
              padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
              borderRadius: tokens.radius.sm,
              background: tokens.color.surface[1],
              color: tokens.color.semantic.warning,
              border: `${tokens.border.width.thin}px solid ${tokens.color.semantic.warning}`,
              fontSize: tokens.font.size.xs,
              fontWeight: tokens.font.weight.semibold,
            }}
            title="This model was converted without vertex colors. Reconvert for best quality."
          >
            <Icon name="warning" size={16} decorative />
            No colors
          </span>
        ) : null}
      </div>

      <div style={{ padding: tokens.space[3], flex: '1 1 auto' }}>
        <div style={{ fontWeight: tokens.font.weight.semibold, fontSize: tokens.font.size.md, marginBottom: tokens.space[1] }}>
          {model.name}
        </div>
        <div
          style={{
            fontFamily: tokens.font.family.mono,
            fontSize: tokens.font.size.xs,
            color: tokens.color.ink.muted,
            marginBottom: tokens.space[2],
            wordBreak: 'break-all',
          }}
        >
          {model.dirPath}
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: `${tokens.space[1]}px ${tokens.space[3]}px`,
            fontSize: tokens.font.size.xs,
          }}
        >
          <StatRow label="Total Size" value={formatBytes(model.totalSize)} />
          <StatRow label="Files" value={String(fileCount)} />
          {meta ? (
            <>
              <StatRow label="Tiles" value={String(meta.tileCount)} />
              <StatRow label="LOD Levels" value={String(meta.lodLevels)} />
              <StatRow label="Version" value={meta.version} />
              <StatRow label="GLB Data" value={formatBytes(meta.totalGlbSize)} />
              {meta.bbox.sx > 0 ? (
                <StatRow
                  label="Bounds"
                  value={`${meta.bbox.sx.toFixed(1)} \u00D7 ${meta.bbox.sy.toFixed(1)} \u00D7 ${meta.bbox.sz.toFixed(1)}`}
                  span
                />
              ) : null}
            </>
          ) : model.metaLoading ? (
            <StatRow label="Loading" value="…" />
          ) : null}
        </div>

        {model.mtime ? (
          <div style={{ marginTop: tokens.space[2], fontSize: tokens.font.size.xs, color: tokens.color.ink.muted }}>
            {new Date(model.mtime).toLocaleString()}
          </div>
        ) : null}
      </div>

      <div style={{ padding: `${tokens.space[2]}px ${tokens.space[3]}px`, borderTop: tokens.border.thin }}>
        <button
          type="button"
          disabled={opening}
          onClick={onSelect}
          style={{
            width: '100%',
            display: 'inline-flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: tokens.space[2],
            padding: `${tokens.space[2]}px 0`,
            background: opening ? tokens.color.surface[0] : tokens.color.accent.primary,
            color: opening ? tokens.color.ink.muted : tokens.color.accent.on,
            border: `${tokens.border.width.thin}px solid transparent`,
            borderRadius: tokens.radius.input,
            cursor: opening ? 'wait' : 'pointer',
            fontFamily: tokens.font.family.ui,
            fontSize: tokens.font.size.sm,
            fontWeight: tokens.font.weight.medium,
            minHeight: tokens.density.touchMin,
          }}
        >
          <Icon name="open_in_new" size={20} decorative />
          {opening ? 'Opening…' : 'Open in viewer'}
        </button>
      </div>
    </div>
  );
}

function StatRow({ label, value, span }: { label: string; value: string; span?: boolean }) {
  return (
    <div style={{ gridColumn: span ? '1 / -1' : undefined, display: 'flex', justifyContent: 'space-between' }}>
      <span style={{ color: tokens.color.ink.muted }}>{label}</span>
      <span style={{ color: tokens.color.ink.secondary, fontWeight: tokens.font.weight.medium }}>{value}</span>
    </div>
  );
}

function ConverterPicker({
  models,
  error,
  openingPath,
  onClose,
  onSelect,
  onReload,
}: ConverterPickerProps) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Open converter output"
      style={{
        position: 'fixed',
        inset: 0,
        background: tokens.color.overlay.scrim,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: tokens.zIndex.modal,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: `min(${tokens.layout.standaloneStateExtraWide + tokens.layout.collapsedRail * 10}px, 94vw)`,
          maxHeight: '86vh',
          overflow: 'auto',
          background: tokens.color.surface[1],
          color: tokens.color.ink.primary,
          borderRadius: tokens.radius.lg,
          padding: tokens.space[5],
          boxShadow: tokens.elevation[3],
          border: tokens.border.thin,
          fontFamily: tokens.font.family.ui,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: tokens.space[3],
            gap: tokens.space[3],
            flexWrap: 'wrap',
          }}
        >
          <h2 style={{ margin: 0, fontSize: tokens.font.size.xl }}>
            Converter outputs
          </h2>
          <div style={{ display: 'inline-flex', gap: tokens.space[2], alignItems: 'center' }}>
            <button
              type="button"
              onClick={onReload}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: tokens.space[1],
                padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
                background: tokens.color.surface[0],
                color: tokens.color.ink.primary,
                border: tokens.border.thin,
                borderRadius: tokens.radius.input,
                cursor: 'pointer',
                fontSize: tokens.font.size.sm,
                fontFamily: tokens.font.family.ui,
                minHeight: tokens.density.touchMin,
              }}
            >
              <Icon name="refresh" size={16} decorative />
              Refresh
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close converter picker"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: tokens.space[1],
                padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
                background: tokens.color.surface[0],
                color: tokens.color.ink.primary,
                border: tokens.border.thin,
                borderRadius: tokens.radius.input,
                cursor: 'pointer',
                fontSize: tokens.font.size.sm,
                fontFamily: tokens.font.family.ui,
                minHeight: tokens.density.touchMin,
              }}
            >
              <Icon name="close" size={16} decorative />
              Close
            </button>
          </div>
        </div>

        <p style={{ margin: 0, marginBottom: tokens.space[4], color: tokens.color.ink.secondary, fontSize: tokens.font.size.sm }}>
          Select a converted output to open in the viewer. Models tagged{' '}
          <strong style={{ color: tokens.color.semantic.warning }}>No colors</strong> were
          converted with an older pipeline; reconvert for best quality.
        </p>

        {error ? (
          <Banner severity="error" title="Converter API error" body={error} />
        ) : null}

        {models === null ? (
          <p
            role="status"
            aria-live="polite"
            style={{ color: tokens.color.ink.secondary, display: 'inline-flex', alignItems: 'center', gap: tokens.space[2] }}
          >
            <Icon name="refresh" size={20} decorative />
            Loading converter outputs…
          </p>
        ) : models.length === 0 ? (
          <p style={{ color: tokens.color.ink.secondary, fontSize: tokens.font.size.sm }}>
            No converted outputs found. Run a conversion in GeoConverter first.
          </p>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(auto-fill, minmax(${tokens.layout.settingsRail}px, 1fr))`,
              gap: tokens.space[3],
            }}
          >
            {models.map((m) => (
              <ModelCard
                key={m.id}
                model={m}
                opening={openingPath === m.tilesetEntry.path}
                onSelect={() => onSelect(m)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
